import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/AuthLayout";
import { TotpEnroll, TotpVerify } from "@/features/security/Totp";
import { mfaStepOf, useSession, verifiedTotpOf } from "@/lib/auth";
import { isDemoMode, supabase } from "@/lib/supabase";

// Two-step sign-in (docs/design/security.md): the _app guard sends a
// password-only session here when the user has an authenticator, or when the
// organization requires one and it is not set up yet. Ends with an aal2
// session and goes on to the page that was asked for.

interface MfaSearch {
  redirect?: string | undefined;
}

export const Route = createFileRoute("/mfa")({
  validateSearch: (raw: Record<string, unknown>): MfaSearch => ({
    // Only same-origin paths (open-redirect guard, as on /login).
    redirect: typeof raw.redirect === "string" && raw.redirect.startsWith("/") && !raw.redirect.startsWith("//") ? raw.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    if (isDemoMode) throw redirect({ to: "/" });
    const session = await context.auth.getSession();
    if (!session) throw redirect({ to: "/login" });
    if (!mfaStepOf(session)) throw redirect({ to: search.redirect ?? "/" });
  },
  component: MfaPage,
});

function MfaPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const session = useSession();
  const step = mfaStepOf(session);
  const factor = verifiedTotpOf(session);

  const done = async () => {
    // The verified challenge returned an aal2 session; fetch the tenant data again under it.
    queryClient.clear();
    await router.invalidate();
    await navigate({ to: search.redirect ?? "/" });
  };
  const signOut = async () => {
    await supabase?.auth.signOut();
    queryClient.clear();
    await navigate({ to: "/login" });
  };

  return (
    <AuthLayout
      title={step === "enroll" ? t("mfa.enrollTitle") : t("mfa.verifyTitle")}
      subtitle={step === "enroll" ? t("mfa.enrollSubtitle") : t("mfa.verifySubtitle")}
    >
      {step === "verify" && factor ? <TotpVerify factorId={factor.id} onDone={done} /> : step === "enroll" ? <TotpEnroll onDone={done} /> : null}
      <button type="button" onClick={() => void signOut()} className="mt-4 self-center text-[13px] font-medium text-ink-muted hover:underline">
        {t("mfa.signOut")}
      </button>
    </AuthLayout>
  );
}
